import { useEffect, useState } from 'react'
import { api } from '../../api/client'
import { SkeletonCard } from '../common/Skeleton'

interface AnalyticsSummary {
  total_events: number
  unique_users: number
  page_views: Record<string, number>
  feature_usage: Record<string, number>
  recent_feedback: Array<{
    id: number
    user_id: string
    category: string
    message: string
    created_at: string
  }>
}

export function AdminPage() {
  const [data, setData] = useState<AnalyticsSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    setLoading(true)
    api
      .get<AnalyticsSummary>('/api/admin/analytics/summary')
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-white">Admin Dashboard</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-white">Admin Dashboard</h2>
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-6 text-center">
          <p className="text-sm font-medium text-rose-400">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-white">Admin Dashboard</h2>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-6">
          <p className="text-sm text-slate-400">Total Events</p>
          <p className="mt-1 text-2xl font-bold text-white">{data?.total_events ?? 0}</p>
        </div>
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-6">
          <p className="text-sm text-slate-400">Unique Users</p>
          <p className="mt-1 text-2xl font-bold text-indigo-400">{data?.unique_users ?? 0}</p>
        </div>
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-6">
          <p className="text-sm text-slate-400">Feedback Entries</p>
          <p className="mt-1 text-2xl font-bold text-amber-400">{data?.recent_feedback?.length ?? 0}</p>
        </div>
      </div>

      {/* Page Views */}
      <section className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-6">
        <h3 className="mb-4 text-lg font-semibold text-white">Page Views</h3>
        {data?.page_views && Object.keys(data.page_views).length > 0 ? (
          <div className="space-y-2">
            {Object.entries(data.page_views)
              .sort(([, a], [, b]) => b - a)
              .map(([page, count]) => (
                <div key={page} className="flex items-center justify-between rounded-lg bg-slate-900/50 px-4 py-2">
                  <span className="text-sm font-medium text-slate-300">{page}</span>
                  <span className="text-sm font-bold text-white">{count}</span>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No page view data yet.</p>
        )}
      </section>

      {/* Feature Usage */}
      <section className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-6">
        <h3 className="mb-4 text-lg font-semibold text-white">Feature Usage</h3>
        {data?.feature_usage && Object.keys(data.feature_usage).length > 0 ? (
          <div className="space-y-2">
            {Object.entries(data.feature_usage)
              .sort(([, a], [, b]) => b - a)
              .map(([feature, count]) => (
                <div key={feature} className="flex items-center justify-between rounded-lg bg-slate-900/50 px-4 py-2">
                  <span className="text-sm font-medium text-slate-300">{feature}</span>
                  <span className="text-sm font-bold text-white">{count}</span>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No feature usage data yet.</p>
        )}
      </section>

      {/* Recent Feedback */}
      <section className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-6">
        <h3 className="mb-4 text-lg font-semibold text-white">Recent Feedback</h3>
        {data?.recent_feedback && data.recent_feedback.length > 0 ? (
          <div className="space-y-3">
            {data.recent_feedback.map((fb) => (
              <div key={fb.id} className="rounded-lg border border-slate-700/50 bg-slate-900/50 p-4">
                <div className="mb-1 flex items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      fb.category === 'bug'
                        ? 'bg-rose-500/20 text-rose-400'
                        : fb.category === 'suggestion'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-slate-700 text-slate-400'
                    }`}
                  >
                    {fb.category}
                  </span>
                  <span className="text-xs text-slate-500">{fb.created_at}</span>
                </div>
                <p className="text-sm text-slate-300">{fb.message}</p>
                <p className="mt-1 text-xs text-slate-500">User: {fb.user_id.slice(0, 8)}…</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">No feedback submitted yet.</p>
        )}
      </section>
    </div>
  )
}
